import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, Trash2, MapPin, Calendar, Tag, Image as ImageIcon, X, Pencil, Clock, Package } from "lucide-react";
import { parseLocalDate } from "../utils/date";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function BandeoDetail() {
  const { id } = useParams();
  const { authHeaders, token, user } = useAuth();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bandeos/${id}`, { headers: authHeaders() });
        setB(data);
      } catch {
        toast.error("Bandeo no encontrado");
        navigate("/bandeos");
      }
      setLoading(false);
    })();
  }, [id]);

  const del = async () => {
    try {
      await axios.delete(`${API}/bandeos/${id}`, { headers: authHeaders() });
      toast.success("Bandeo eliminado");
      navigate("/bandeos");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  if (loading) return <div className="p-10 kbd-label text-slate-400">Cargando…</div>;
  if (!b) return null;

  const photoUrl = (path) => `${API}/files/${path}`;

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="bandeo-detail-page">
      <button onClick={() => navigate(-1)} className="kbd-label flex items-center gap-2 mb-6 text-slate-500 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="kbd-label mb-2 text-emerald-700">/ Bandeo</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-950">{b.chain}</h1>
          <div className="flex flex-wrap gap-2 mt-4 text-sm text-slate-600">
            <InfoChip icon={Calendar} label={parseLocalDate(b.fecha).toLocaleDateString("es", { day: "numeric", month: "long", year: "numeric" })} />
            <InfoChip icon={MapPin} label={b.branch} />
            {b.arrival_time && <InfoChip icon={Clock} label={`Llegada ${b.arrival_time}`} />}
            {b.departure_time && <InfoChip icon={Clock} label={`Salida ${b.departure_time}`} />}
            <InfoChip icon={Tag} label={b.user_name} />
          </div>
        </div>
        {(user?.role === "admin" || user?.id === b.user_id) && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/bandeos/${id}/edit`)} className="gap-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50" data-testid="edit-bandeo-btn">
              <Pencil className="w-4 h-4" /> Editar
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" data-testid="delete-bandeo-btn">
                  <Trash2 className="w-4 h-4" /> Eliminar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar bandeo?</AlertDialogTitle>
                  <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5 tv-ticker-line pl-6">
          <div className="kbd-label">Cajas / Unidades entregadas</div>
          <p className="text-slate-900 mt-2 leading-relaxed">{b.delivery_qty || "—"}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 tv-ticker-line pl-6">
          <div className="kbd-label flex items-center gap-2"><Package className="w-3 h-3" /> Atado</div>
          <p className="text-slate-900 mt-2 leading-relaxed">{b.product_banded} → {b.banded_to_product}</p>
        </div>
        {b.comments && (
          <div className="md:col-span-2 bg-white border border-slate-200 rounded-lg p-5 tv-ticker-line pl-6">
            <div className="kbd-label">Comentarios</div>
            <p className="text-slate-900 mt-2 whitespace-pre-wrap leading-relaxed">{b.comments}</p>
          </div>
        )}
        {user?.role === "admin" && (b.arrival_lat != null && b.arrival_lng != null) && (
          <div className="md:col-span-2 bg-white border border-slate-200 rounded-lg p-5 tv-ticker-line pl-6" data-testid="admin-geo-section">
            <div className="kbd-label flex items-center gap-2 text-emerald-700">
              <MapPin className="w-3 h-3" /> Ubicación al llegar (solo admin)
            </div>
            <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-sm text-slate-700">
                <span className="tabular-nums">
                  {b.arrival_lat.toFixed(6)}, {b.arrival_lng.toFixed(6)}
                </span>
                {b.arrival_accuracy != null && (
                  <span className="ml-2 text-slate-500 text-xs">
                    (±{Math.round(b.arrival_accuracy)} m)
                  </span>
                )}
              </div>
              <a
                href={`https://www.google.com/maps?q=${b.arrival_lat},${b.arrival_lng}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 kbd-label text-emerald-700 hover:text-emerald-900 underline underline-offset-4"
                data-testid="admin-geo-map-link"
              >
                <MapPin className="w-3 h-3" /> Ver en Google Maps
              </a>
            </div>
          </div>
        )}
      </div>

      {b.delivery_doc_photos?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 md:p-6 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="w-4 h-4 text-slate-400" />
            <div className="kbd-label">/ Documento de entrega · {b.delivery_doc_photos.length}</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {b.delivery_doc_photos.map((path, i) => (
              <button key={path} onClick={() => setLightbox(path)} className="aspect-square bg-slate-100 rounded-md overflow-hidden hover:ring-2 hover:ring-emerald-600" data-testid={`doc-photo-${i}`}>
                <img src={photoUrl(path)} alt={`doc ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {b.tying_photos?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 md:p-6">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="w-4 h-4 text-slate-400" />
            <div className="kbd-label">/ Registro de atados · {b.tying_photos.length}</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {b.tying_photos.map((path, i) => (
              <button key={path} onClick={() => setLightbox(path)} className="aspect-square bg-slate-100 rounded-md overflow-hidden hover:ring-2 hover:ring-emerald-600" data-testid={`tying-photo-${i}`}>
                <img src={photoUrl(path)} alt={`atado ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-slate-950/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center"><X className="w-5 h-5" /></button>
          <img src={photoUrl(lightbox)} alt="full" className="max-h-[90vh] max-w-[90vw] object-contain" />
        </div>
      )}
    </div>
  );
}

function InfoChip({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-md">
      <Icon className="w-3.5 h-3.5 text-slate-500" />
      <span className="text-xs font-medium text-slate-800">{label}</span>
    </div>
  );
}
